import { isSuccess } from '@devexperts/remote-data-ts'
import { Button, cn } from '@spiaggeit/spit-ui'
import { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '@/assets/icons'
import { ButtonLink } from '@/components/ButtonLink'
import { KioskTimeoutDialog } from '@/components/Dialog/KioskTimeoutDialog'
import { DownloadConfirmationButton } from '@/components/DownloadConfirmationButton'
import { GridWithRecap } from '@/components/GridWithRecap'
import { LoaderTrigger } from '@/components/LoaderTrigger'
import { INSERT_PERIOD_PATH } from '@/app/router/paths.ts'
import { useAppDispatch, useAppSelector } from '@/hooks/store.ts'
import { useAppPath } from '@/hooks/useAppPath.ts'
import { useBackGuard } from '@/hooks/useBackGuard'
import { useCartRecapData } from '@/hooks/useCartRecapData'
import { useHostProfileUrl } from '@/hooks/useHostProfileUrl.ts'
import { useWhiteLabelBgTextClassName } from '@/hooks/useWhiteLabelBgTextClassName.ts'
import { httpClient } from '@/lib/http/HttpClient'
import { ThankYouCards } from '@/routes/ThankYou/Cards'
import { ThankYouRecap } from '@/routes/ThankYou/Recap'
import {
  appSlice,
  isAppSelector,
  isKioskSelector,
  isWebsiteSelector,
  isWidgetSelector,
} from '@/store/appSlice'
import { cartSlice } from '@/store/cartSlice'
import { selectTotal } from '@/store/cartSlice/selectors/selectTotalPrice'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { kioskSlice } from '@/store/kioskSlice'
import { licenseSlice } from '@/store/licenseSlice'
import {
  NO,
  ONE_SECOND,
  PAY_QUERY_PARAM_NAME,
  REG_ID_QUERY_PARAM_NAME,
  YES,
} from '@/utils/constants.ts'

const TEN_SECONDS = ONE_SECOND * 10

export const ThankYou = () => {
  useBackGuard()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const reservationId = searchParams.get(REG_ID_QUERY_PARAM_NAME)
  const insertPeriodPath = useAppPath(INSERT_PERIOD_PATH)
  const navigate = useNavigate()
  const paymentLinkId = searchParams.get(PAY_QUERY_PARAM_NAME)
  const isKiosk = useAppSelector(isKioskSelector)
  const isAppMode = useAppSelector(isAppSelector)
  const isWebsiteMode = useAppSelector(isWebsiteSelector)
  const isWidgetMode = useAppSelector(isWidgetSelector)
  const cartDetail = useAppSelector(cartSlice.selectors.cartDetail)
  const total = useAppSelector((state) => selectTotal(state, true))
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const { deposit } = useCartRecapData()
  const country = useAppSelector(appSlice.selectors.country)
  const license = useAppSelector(licenseSlice.selectors.license)
  const headingClassName = useWhiteLabelBgTextClassName(
    cn('mb-2 text-xl font-bold', { 'mb-0': isKiosk })
  )
  const paragraphClassName = useWhiteLabelBgTextClassName('mb-6 lg:mb-8')

  const { hostUrl, profileUrl } = useHostProfileUrl()

  const hasPushedDataLayer = useRef(false)

  useEffect(() => {
    if (license && isSuccess(cartDetail) && !hasPushedDataLayer.current) {
      const stripeFees = cartDetail.value.cart.reservationModels.reduce(
        (acc, curr) => acc + curr.online_payment_fees,
        0
      )

      const stripeCost = cartDetail.value.cart.reservationModels.reduce(
        (acc, curr) => acc + curr.portal_payment_fees,
        0
      )

      const transactionAmount = cartDetail.value.cart.reservationModels.reduce(
        (acc, curr) => {
          if (curr.forced_total) {
            return acc + curr.forced_total
          } else if (curr.forced_beach) {
            return acc + (curr.forced_beach - curr.list_beach)
          } else {
            return curr.list_total
          }
        },
        0
      )

      const cartNetValue = total - stripeFees
      window.dataLayer.push({
        Acconto: deposit > 0 ? YES : NO,
        'Assicurazione meteo': cartDetail.value.cart?.weatherPolicy?.price
          ? YES
          : NO,
        'Commissione Spiagge': cartDetail.value.cart.totalFeesValue,
        'Commissione transazione': stripeFees,
        'Costo Stripe': stripeCost,
        Margine: stripeFees + (10 * 100) / cartNetValue - stripeCost,
        'Numero giorni': dayDifference,
        'Percentuale acconto': (deposit / total) * 100,
        Transato: transactionAmount,
        'Valore carrello': total,
        'Valore carrello netto': cartNetValue,
      })

      window.dataLayer.push({
        coupon: cartDetail.value.cart.totalDiscountValue,
        currency: 'EUR',
        event: 'purchase',
        transaction_id: cartDetail.value.cart.order,
        value: cartDetail.value.cart.totalPrice,
      })

      httpClient.fetch('search/reservation', country, {
        body: JSON.stringify({
          beachId: license.id,
          orderId: cartDetail.value.cart.order,
          reservationValue: cartDetail.value.cart.totalPrice,
          sid: searchParams.get('sid') ?? '',
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })

      hasPushedDataLayer.current = true
    }
  }, [cartDetail, license])

  useEffect(() => {
    dispatch(cartSlice.actions.setIsCartPaid(true))

    if (isKiosk) {
      dispatch(kioskSlice.actions.resetKiosk())
    }

    if (reservationId) {
      if (isAppMode) {
        window.ReactNativeWebView.postMessage('{"event":"reservation-booking"}')
      }

      dispatch(
        cartSlice.actions.getCartDetail({
          reservationId,
          ...(typeof paymentLinkId === 'string'
            ? {
                paymentLinkId,
              }
            : {}),
        })
      )
      return
    }

    navigate(insertPeriodPath)
  }, [isKiosk, reservationId, isAppMode, insertPeriodPath, dispatch, navigate])

  if (!isSuccess(cartDetail)) {
    return <LoaderTrigger />
  }

  const [{ first_name: userName }] = cartDetail.value.cart.reservationModels

  const getHeading = () => {
    if (isKiosk) {
      return t('kiosk.payment.printDialog.title')
    }

    if (
      cartDetail.value.paymentLink &&
      cartDetail.value.paymentLink.amount !== total
    ) {
      return t('thankYou.depositTitle', { userName })
    }

    return t('thankYou.title', { userName })
  }

  return (
    <>
      <div className="container grow py-4 lg:py-8">
        {userName && <h1 className={headingClassName}>{getHeading()}</h1>}

        {isWidgetMode ? (
          <p className={paragraphClassName}>
            {t('thankYou.widgetDescription')}
          </p>
        ) : null}

        {isWebsiteMode || isAppMode ? (
          <p className={paragraphClassName}>
            <Trans i18nKey="thankYou.portalDescription">
              <Link className="text-link" to={profileUrl} />
            </Trans>
          </p>
        ) : null}

        <div className={cn('flex flex-col gap-4', { 'mt-6': isKiosk })}>
          <GridWithRecap className="lg:mt-0">
            <GridWithRecap.ContentContainer
              className={cn('gap-4', { 'col-span-3 lg:col-span-3': isKiosk })}
            >
              <ThankYouRecap />

              <div className="flex flex-col gap-4 lg:flex-row">
                {!isKiosk && <DownloadConfirmationButton variant="outline" />}

                {isWebsiteMode && (
                  <ButtonLink
                    className="w-full"
                    icon="HomeIcon"
                    iconOnLeftSide
                    label={t('thankYou.backToHome.website')}
                    to={hostUrl}
                  />
                )}

                {isAppMode && (
                  <Button
                    className="w-full"
                    onClick={() => {
                      window.ReactNativeWebView.postMessage(
                        '{"event":"go-to-page","page":"reservations"}'
                      )
                    }}
                  >
                    <Icon className="size-5" name="HomeIcon" />
                    {t('thankYou.backToHome.website')}
                  </Button>
                )}
              </div>
            </GridWithRecap.ContentContainer>

            <GridWithRecap.RecapContainer>
              <ThankYouCards />
            </GridWithRecap.RecapContainer>
          </GridWithRecap>
        </div>
      </div>
      <KioskTimeoutDialog timeout={TEN_SECONDS} />
    </>
  )
}
